import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import ResourcesLoader from '../../containers/Editor/ResourcesLoader';
import Header from '../../containers/Editor/Header';
import ThumbnailsColumn from '../../containers/Editor/Thumbnails/Column';
import Spread from '../../containers/Editor/Spread';
import PreviewPagesSpread from '../../containers/Editor/PreviewPagesSpread';
import ProcessingLoader from '../../containers/Editor/ProcessingLoader';
import LockedEditorPopup from '../../containers/Editor/LockedEditorPopup';
import Spinner from './Spinner';
import {
	EditorContainer,
	EditorWrapper,
	ContentWrapper,
	ContentInner,
	SpreadWrapper,
	Loader,
} from './styles';
import { getData, reset, updateThumbnailsIfNeeded } from '../../containers/Editor/actions';
import { EditorType } from '../../containers/Editor/constants';
import { updateDriveItem } from '../../containers/DriveItemList/actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import FromApp from '../../containers/ResourceSelector/DrivePicker/FromApp';
import Utils from '../../utils';
import DriveItemUtils from '../../utils/driveItem';
import { NewStyleColor } from '../../utils/styles-constants';
import { setIsAccessibleVersion } from '../../containers/Editor/actions/ui';

interface EditorProps {
	type: EditorType;
	clientId: number;
	originalGId: number;
	tempGId: number;
	isEditorLoaded: boolean;
	isPreviewPagesSpread: boolean;
	isProcessing: boolean;
	dispatch: Dispatch<StoreState>;
	isAccessibleVersion: boolean;
}

const Editor = ({
	type,
	clientId,
	originalGId,
	tempGId,
	isEditorLoaded,
	isPreviewPagesSpread,
	isProcessing,
	dispatch,
	isAccessibleVersion,
}: EditorProps) => {
	const container = React.useRef<HTMLDivElement>(null);
	const tempGIdRef = React.useRef(tempGId);

	// need for resource refresh if it was previously downloaded
	const updateResourceMeta = async () => {
		const resource = await ApiUtils.getMeta(originalGId);
		dispatch(updateDriveItem(resource));
	};

	React.useEffect(() => {
		dispatch(setIsAccessibleVersion(isAccessibleVersion));
		dispatch(getData(clientId, originalGId));

		return () => {
			try {
				dispatch(reset());
				ApiUtils.releaseLock(clientId, tempGIdRef.current);
				dispatch(updateThumbnailsIfNeeded(clientId, tempGIdRef.current));
				updateResourceMeta();
			// tslint:disable-next-line
			} catch (error) {}
		};
	}, [clientId, originalGId, isAccessibleVersion]);

	React.useEffect(() => {
		// ref is used to prevent a call of useEffect above
		tempGIdRef.current = tempGId;
	}, [tempGId]);

	return (
		<>
			{Utils.isEmptyPlaylistMode()
				?
				<FromApp
					isValidResource={DriveItemUtils.isValidResourceForPlaylist}
					onSelect={() => null}
					onClose={() => null}
					actionButtonColor={NewStyleColor.Blue450}
					isMultipleSelection={false}
				/>
				:
				<>
					<ResourcesLoader />
					<EditorContainer ref={container}>
						<Header isEditorLoaded={isEditorLoaded} />
						<EditorWrapper>
							{isEditorLoaded
								? (
									<>
										{isProcessing && <ProcessingLoader />}
										{type === EditorType.INDESIGN && <ThumbnailsColumn />}
										<ContentWrapper>
											<ContentInner>
												<SpreadWrapper>
													{isPreviewPagesSpread && <PreviewPagesSpread />}
													<Spread
														isHidden={isPreviewPagesSpread}
														editorContainer={container.current}
													/>
												</SpreadWrapper>
											</ContentInner>
										</ContentWrapper>
									</>
								)
								: (
									<Loader>
										<Spinner />
										<Translate value="messages.loading" />
									</Loader>
								)
							}
						</EditorWrapper>
					</EditorContainer>
					<LockedEditorPopup />
				</>
			}
		</>
	);
};

export default Editor;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/index.tsx